import React from "react";
import { useI18n } from "../../i18n";
import {
  Globe2,
  CalendarCheck2,
  UtensilsCrossed,
  MapPin,
  Smartphone,
  ShieldCheck,
} from "lucide-react";

const FEATURES_IMG =
  "https://images.unsplash.com/photo-1689037676470-b72230d5236e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwY2FmZSUyMGludGVyaW9yfGVufDB8fHx8MTc3OTAwNDg0MXww&ixlib=rb-4.1.0&q=85";

export default function Features() {
  const { t } = useI18n();

  const items = [
    { Icon: Globe2, t: t("features.f1_t"), d: t("features.f1_d") },
    { Icon: CalendarCheck2, t: t("features.f2_t"), d: t("features.f2_d") },
    { Icon: UtensilsCrossed, t: t("features.f3_t"), d: t("features.f3_d") },
    { Icon: MapPin, t: t("features.f4_t"), d: t("features.f4_d") },
    { Icon: Smartphone, t: t("features.f5_t"), d: t("features.f5_d") },
    { Icon: ShieldCheck, t: t("features.f6_t"), d: t("features.f6_d") },
  ];

  return (
    <section id="features" className="section-y bg-brand-sand" data-testid="features-section">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
          <div className="md:col-span-7">
            <p className="eyebrow reveal">{t("features.eyebrow")}</p>
            <h2 className="display-h2 mt-4 text-brand-ink reveal" data-delay="1" data-testid="features-title">
              {t("features.title")}
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-8">
            <p className="body-lg reveal" data-delay="2">{t("features.lead")}</p>
          </div>
        </div>

        {/* Bento grid: 6 cards in 12-col layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[1fr]">
          {/* Large feature 1 — Online reservations (spotlight) */}
          <article
            className="md:col-span-7 reveal card-surface overflow-hidden p-0 group"
            data-testid="feature-card-reservations"
          >
            <div className="grid grid-cols-1 md:grid-cols-5 h-full">
              <div className="md:col-span-3 p-8 md:p-10 flex flex-col">
                <CalendarCheck2 size={28} strokeWidth={1.6} className="text-brand-terracotta" />
                <h3 className="display-h3 mt-5 text-brand-ink">{items[1].t}</h3>
                <p className="body-lg mt-3">{items[1].d}</p>
                <div className="mt-auto pt-8 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-brand-mute">
                  <span className="h-px w-8 bg-brand-line" />
                  Zero commission
                </div>
              </div>
              <div className="md:col-span-2 relative min-h-[220px] md:min-h-0 bg-brand-stone">
                <img
                  src={FEATURES_IMG}
                  alt="Cosy restaurant interior with set tables"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </article>

          {/* Branded website */}
          <article className="md:col-span-5 reveal card-surface p-8 md:p-10" data-delay="1" data-testid="feature-card-branded">
            <Globe2 size={26} strokeWidth={1.6} className="text-brand-forest" />
            <h3 className="display-h3 mt-5 text-brand-ink">{items[0].t}</h3>
            <p className="body-lg mt-3">{items[0].d}</p>
          </article>

          {/* Live menu */}
          <article className="md:col-span-4 reveal card-surface p-8 md:p-10" data-testid="feature-card-menu">
            <UtensilsCrossed size={26} strokeWidth={1.6} className="text-brand-forest" />
            <h3 className="display-h3 mt-5 text-brand-ink">{items[2].t}</h3>
            <p className="body-lg mt-3">{items[2].d}</p>
          </article>

          {/* SEO */}
          <article className="md:col-span-4 reveal card-surface p-8 md:p-10" data-delay="1" data-testid="feature-card-seo">
            <MapPin size={26} strokeWidth={1.6} className="text-brand-forest" />
            <h3 className="display-h3 mt-5 text-brand-ink">{items[3].t}</h3>
            <p className="body-lg mt-3">{items[3].d}</p>
          </article>

          {/* Mobile-first */}
          <article className="md:col-span-4 reveal card-surface p-8 md:p-10" data-delay="2" data-testid="feature-card-mobile">
            <Smartphone size={26} strokeWidth={1.6} className="text-brand-forest" />
            <h3 className="display-h3 mt-5 text-brand-ink">{items[4].t}</h3>
            <p className="body-lg mt-3">{items[4].d}</p>
          </article>

          {/* GDPR — dark accent card */}
          <article
            className="md:col-span-12 reveal rounded-2xl overflow-hidden bg-brand-forest text-brand-ivory p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
            data-testid="feature-card-gdpr"
          >
            <div className="md:col-span-2 flex md:justify-center">
              <ShieldCheck size={40} strokeWidth={1.4} className="text-brand-terracotta" />
            </div>
            <div className="md:col-span-7">
              <h3 className="display-h3 text-brand-ivory">{items[5].t}</h3>
              <p className="body-lg !text-brand-ivory/80 mt-3">{items[5].d}</p>
            </div>
            <div className="md:col-span-3 text-sm text-brand-ivory/70 md:text-right">
              <span className="inline-block px-3 py-1.5 rounded-full border border-brand-ivory/30 uppercase tracking-[0.18em] text-[11px]">
                Built for Europe
              </span>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
